import type { DriverParcel, DriverStop } from '../api/types';

/**
 * Matchen gebeurt op het **staartstuk van 12 tekens** (contract §4.3): de plaatscode in het
 * `ParcelNumber` volgt de bestemming (D75) en verschilt dus per stad binnen één rit. Naar de
 * server gaat altijd het volledige nummer.
 */
export const TAIL_LENGTH = 12;

/** D95: `O` en `0` zitten niet in het alfabet — juist om overtypen mogelijk te maken. */
const ALLOWED_CHAR = /^[1-9A-NP-Z]$/;

/** De twee tekens die de bezorger verwart; hij krijgt daar de O-regel bij te zien. */
const CONFUSABLE = /^[O0]$/;

export const FULL_PARCEL_PATTERN =
  /^GBNL-\d{4}-[A-Z]{3}-[1-9A-NP-Z]{4}-[1-9A-NP-Z]{4}-[1-9A-NP-Z]{4}$/;

/** Alles wat geen letter of cijfer is eruit, hoofdletters. */
export function normaliseCode(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** De laatste 12 tekens van een genormaliseerd nummer. */
export function parcelTail(parcelNumber: string): string {
  const normalised = normaliseCode(parcelNumber);
  return normalised.slice(-TAIL_LENGTH);
}

/** De vaste kop (`GBNL-2026-AMS-`) van een volledig nummer. */
export function parcelPrefix(parcelNumber: string): string {
  const head = parcelNumber.trim().toUpperCase();
  const match = /^(GBNL-\d{4}-[A-Z]{3}-)/.exec(head);
  return match ? match[1]! : '';
}

/** `…-353N-33AC-853A` — de lijstweergave van D1 en E2. */
export function shortParcelLabel(parcelNumber: string): string {
  const tail = parcelTail(parcelNumber);
  if (tail.length < TAIL_LENGTH) return parcelNumber;
  return `…-${tail.slice(0, 4)}-${tail.slice(4, 8)}-${tail.slice(8, 12)}`;
}

/** `353N 33AC 853A` — de groepering in het invoerveld. */
export function formatTailGroups(tail: string): string {
  return (tail.match(/.{1,4}/g) ?? []).join(' ');
}

export interface SanitisedInput {
  /** De aanvaarde tekens, hoofdletters, maximaal `TAIL_LENGTH`. */
  value: string;
  /** Er is een `O` of `0` getypt: die wordt geweigerd, nooit omgezet. */
  rejectedConfusable: boolean;
}

/**
 * Filtert de invoer van G1. Een getypte `O` wordt geweigerd met de O-regel uit `copy.md`,
 * niet stilzwijgend omgezet — automatisch omzetten verbergt dat je het verkeerde label leest.
 */
export function sanitiseTailInput(raw: string, maxLength: number = TAIL_LENGTH): SanitisedInput {
  let value = '';
  let rejectedConfusable = false;
  for (const char of raw.toUpperCase()) {
    if (value.length >= maxLength) break;
    if (ALLOWED_CHAR.test(char)) {
      value += char;
    } else if (CONFUSABLE.test(char)) {
      rejectedConfusable = true;
    }
  }
  return { value, rejectedConfusable };
}

export function isCompleteTail(value: string): boolean {
  return value.length === TAIL_LENGTH;
}

export interface ParcelLocation {
  stop: DriverStop;
  parcel: DriverParcel;
}

/** Zoekt een pakket in de gecachte rit op het staartstuk. Werkt volledig offline. */
export function findParcelByCode(stops: DriverStop[], code: string): ParcelLocation | null {
  const tail = parcelTail(code);
  if (tail.length < TAIL_LENGTH) return null;
  for (const stop of stops) {
    for (const parcel of stop.parcels) {
      if (parcelTail(parcel.parcelNumber) === tail) return { stop, parcel };
    }
  }
  return null;
}

export type DeliverScanOutcome =
  | { kind: 'unknown'; code: string }
  | { kind: 'alreadyDelivered'; stop: DriverStop; parcel: DriverParcel }
  | { kind: 'otherStop'; stop: DriverStop; parcel: DriverParcel }
  | { kind: 'delivered'; stop: DriverStop; parcel: DriverParcel };

/** De vier scanuitkomsten F2/F3 · F4 · F5 · F6 bij het afmelden. */
export function classifyDeliverScan(
  stops: DriverStop[],
  code: string,
  currentStopId: string | null,
): DeliverScanOutcome {
  const found = findParcelByCode(stops, code);
  if (!found) return { kind: 'unknown', code: normaliseCode(code) };
  if (found.parcel.status === 'delivered') {
    return { kind: 'alreadyDelivered', stop: found.stop, parcel: found.parcel };
  }
  if (currentStopId !== null && found.stop.id !== currentStopId) {
    return { kind: 'otherStop', stop: found.stop, parcel: found.parcel };
  }
  return { kind: 'delivered', stop: found.stop, parcel: found.parcel };
}

export type LoadScanOutcome =
  | { kind: 'unknown'; code: string }
  | { kind: 'alreadyLoaded'; stop: DriverStop; parcel: DriverParcel }
  | { kind: 'loaded'; stop: DriverStop; parcel: DriverParcel };

/** De uitkomsten bij het laden (D1–D4). */
export function classifyLoadScan(stops: DriverStop[], code: string): LoadScanOutcome {
  const found = findParcelByCode(stops, code);
  if (!found) return { kind: 'unknown', code: normaliseCode(code) };
  if (found.parcel.loaded) return { kind: 'alreadyLoaded', ...found };
  return { kind: 'loaded', ...found };
}
